<template>
  <div class="item-block" :class="{ 'item-block--expanded': expanded }">
    <!-- Header (collapsed) -->
    <div class="item-block__header" @click="toggle">
      <div class="d-flex align-items-center flex-wrap">
        <router-link
          v-if="item.pica_service_id && item.pica_service_name"
          :to="{ name: 'picaServiceRedirect', params: { picaServiceId: item.pica_service_id }}"
          class="font-weight-medium mr-2 event-link"
          @click.native.stop
        >
          {{ item.pica_service_name }}
        </router-link>
        <span v-else class="font-weight-medium mr-2">
          {{ item.pica_service_name || '-' }}
        </span>
        <span v-if="picacode" class="picacode-tag">
          <CIcon name="cil-qr-code" size="sm" class="mr-1"/>
          <code>{{ picacode }}</code>
        </span>
      </div>
      <div class="d-flex align-items-center">
        <div class="text-right mr-3">
          <span class="font-weight-medium">
            {{ formatAmount(item.price) }} {{ currency }}
          </span>
        </div>
        <CIcon
          :name="expanded ? 'cil-chevron-bottom' : 'cil-chevron-right'"
          size="sm"
        />
      </div>
    </div>

    <!-- Content (expanded) -->
    <CCollapse :show="expanded">
      <div class="item-block__content">
        <PTransparentLoader :loading="itemLoading" v-if="!itemDetail && itemLoading">
          <div style="min-height: 100px;"></div>
        </PTransparentLoader>

        <template v-if="itemDetail">
          <!-- Order Item Card (photos, media) -->
          <div class="item-block__section">
            <OrderItemCard
              :item="itemDetail"
              :currency="currency"
              :can-download="true"
              :can-manage-photos="canManagePhotos"
              :removed-photo-ids="removedPhotoIds"
              @download="onMediaDownload"
              @remove-photos="onRemovePhotos"
              @clear-status-photos="onClearStatusPhotos"
            />
          </div>

          <!-- Refund Box -->
          <div class="item-block__section">
            <RefundBox
              :order-item="itemDetail"
              :scope="scope"
              :removed-photos="removedPhotos"
              :purchased-count="purchasedCount"
              @clear-photos="clearPhotoSelections"
              @select-all-removed="selectAllPurchasedAsRemoved"
            />
          </div>
        </template>
      </div>
    </CCollapse>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import PTransparentLoader from '@/domain/core/components/PTransparentLoader.vue'
import OrderItemCard from './OrderItemCard.vue'
import RefundBox from './RefundBox.vue'

export default {
  name: 'OrderItemBlock',
  components: {
    PTransparentLoader,
    OrderItemCard,
    RefundBox
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    itemDetail: {
      type: Object,
      default: null
    },
    itemLoading: {
      type: Boolean,
      default: false
    },
    scope: {
      type: Object,
      required: true
    },
    currency: {
      type: String,
      default: 'EUR'
    },
    expandedInitially: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      expanded: this.expandedInitially,
      removedPhotos: []
    }
  },
  computed: {
    ...mapGetters('accounting/order', ['getOrderItemMedia']),

    purchasedCount() {
      if (!this.itemDetail) return 0
      const mediaData = this.getOrderItemMedia(this.itemDetail.id)
      return mediaData.media.filter(m => m.is_purchased).length
    },

    picacode() {
      return this.item.picacode || this.itemDetail?.picacode || null
    },

    canManagePhotos() {
      return this.itemDetail && this.itemDetail.refunds !== null
    },

    removedPhotoIds() {
      return this.removedPhotos.map(p => p.id)
    },

  },
  methods: {
    ...mapActions('accounting/order', ['downloadMedia']),

    toggle() {
      this.expanded = !this.expanded
    },

    async onMediaDownload({ mediaIds, orderItemId }) {
      await this.downloadMedia({ orderItemId, mediaIds })
    },

    onRemovePhotos(photos) {
      for (const photo of photos) {
        if (!this.removedPhotos.find(p => p.id === photo.id)) {
          this.removedPhotos.push(photo)
        }
      }
    },

    clearPhotoSelections() {
      this.removedPhotos = []
    },

    onClearStatusPhotos(photoIds) {
      this.removedPhotos = this.removedPhotos.filter(p => !photoIds.includes(p.id))
    },

    selectAllPurchasedAsRemoved() {
      if (!this.itemDetail) return
      const mediaData = this.getOrderItemMedia(this.itemDetail.id)
      const purchasedPhotos = mediaData.media.filter(m => m.is_purchased)
      this.removedPhotos = purchasedPhotos
    },

    formatAmount(amount) {
      const num = parseFloat(amount) || 0
      return num.toFixed(2)
    },

  }
}
</script>

<style scoped>
.item-block__section {
  padding: 16px 0;
}

.item-block__section:last-child {
  padding-bottom: 0;
}

</style>
